import React, { useState, useEffect } from 'react';
import { slpRecords, organizations, venues, studentsWithNumbers } from '../../Data/studentLearningProfiles';
import { slpTypes, eventTypes, achievementLevels, slpCategories } from '../../Data/studentLearningProfiles/slpConfig';
import { teachers } from '../../Data/teachers';

const SLPEventForm = ({ 
  formData, 
  setFormData, 
  onSubmit, 
  onCancel, 
  errors = {}, 
  isEdit = false 
}) => {
  const [showStudentSelector, setShowStudentSelector] = useState(false);
  const [filteredOrganizations, setFilteredOrganizations] = useState(organizations);
  const [filteredVenues, setFilteredVenues] = useState(venues);

  useEffect(() => {
    // Auto-calculate duration when time changes
    if (formData.timeFrom && formData.timeTo) {
      const fromTime = new Date(`2000-01-01T${formData.timeFrom}:00`);
      const toTime = new Date(`2000-01-01T${formData.timeTo}:00`);
      const diffMs = toTime - fromTime;
      const diffHours = diffMs / (1000 * 60 * 60);
      
      if (diffHours > 0) {
        setFormData({ ...formData, duration: diffHours });
      }
    }
  }, [formData.timeFrom, formData.timeTo]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleStudentToggle = (studentNumber) => {
    const currentStudents = formData.students || [];
    const isSelected = currentStudents.includes(studentNumber);
    
    if (isSelected) {
      handleInputChange('students', currentStudents.filter(s => s !== studentNumber));
    } else {
      handleInputChange('students', [...currentStudents, studentNumber]);
    }
  };

  const handleTeacherToggle = (teacherId) => {
    const currentTeachers = formData.teachersInCharge || [];
    const isSelected = currentTeachers.includes(teacherId);
    
    if (isSelected) {
      handleInputChange('teachersInCharge', currentTeachers.filter(t => t !== teacherId));
    } else {
      handleInputChange('teachersInCharge', [...currentTeachers, teacherId]);
    }
  };

  const getSelectedStudentsDisplay = () => {
    if (!formData.students?.length) return '未選擇學生';
    
    return formData.students.map(studentNumber => {
      const student = studentsWithNumbers.find(s => s.studentNumber === studentNumber);
      return student ? `${student.name} (${studentNumber})` : studentNumber;
    }).join(', ');
  };

  const getSelectedTeachersDisplay = () => {
    if (!formData.teachersInCharge?.length) return '未選擇負責老師';
    
    return formData.teachersInCharge.map(teacherId => {
      const teacher = teachers.find(t => t.id === teacherId);
      return teacher ? teacher.name : teacherId;
    }).join(', ');
  };

  return (
    <div style={{ 
      padding: '20px', 
      backgroundColor: '#f8f9fa', 
      minHeight: '100vh',
      fontFamily: 'Arial, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <button 
          onClick={onCancel}
          style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            marginRight: '15px',
            color: '#007bff'
          }}
        >
          ← 
        </button>
        <div>
          <h2 style={{ margin: 0, color: '#333' }}>
            {isEdit ? '編輯 SLP 記錄' : '新增 SLP 記錄'}
          </h2>
          <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
            {formData.slpNumber && `SLP編號: ${formData.slpNumber}`}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          padding: '25px'
        }}>
          {/* Error Display */}
          {errors.general && (
            <div style={{
              padding: '10px',
              backgroundColor: '#f8d7da',
              color: '#721c24',
              borderRadius: '4px',
              marginBottom: '20px'
            }}>
              {errors.general}
            </div>
          )}

          {/* Basic Information */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#333', borderBottom: '2px solid #007bff', paddingBottom: '10px' }}>
              基本資料
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                  日期 *
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: errors.date ? '2px solid #dc3545' : '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  required
                />
                {errors.date && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{errors.date}</div>}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                  SLP類型 *
                </label>
                <select
                  value={formData.slpType}
                  onChange={(e) => handleInputChange('slpType', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  required
                >
                  {Object.entries(slpTypes).map(([key, type]) => (
                    <option key={key} value={key}>
                      {type.icon} {type.name} ({type.nameEn})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                活動類型 *
              </label>
              <select
                value={formData.eventType}
                onChange={(e) => handleInputChange('eventType', e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                required
              >
                {Object.entries(eventTypes).map(([key, type]) => (
                  <option key={key} value={key}>
                    {type.icon} {type.name} ({type.nameEn})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                活動標題 *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="請輸入活動標題"
                style={{
                  width: '100%',
                  padding: '10px',
                  border: errors.title ? '2px solid #dc3545' : '1px solid #ddd',
                  borderRadius: '4px',
                  fontSize: '14px'
                }}
                required
              />
              {errors.title && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{errors.title}</div>}
            </div>
          </div>

          {/* Location and Organization */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#333', borderBottom: '2px solid #28a745', paddingBottom: '10px' }}>
              地點及機構
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                  主辦機構 *
                </label>
                <select
                  value={formData.organization}
                  onChange={(e) => handleInputChange('organization', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: errors.organization ? '2px solid #dc3545' : '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  required
                >
                  <option value="">請選擇機構</option>
                  {filteredOrganizations.map(org => (
                    <option key={org.id} value={org.id}>
                      {org.name} ({org.nameEn})
                    </option>
                  ))}
                </select>
                {errors.organization && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{errors.organization}</div>}
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                  活動地點 *
                </label>
                <select
                  value={formData.venue}
                  onChange={(e) => handleInputChange('venue', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: errors.venue ? '2px solid #dc3545' : '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                  required
                >
                  <option value="">請選擇地點</option>
                  {filteredVenues.map(venue => (
                    <option key={venue.id} value={venue.id}>
                      {venue.name} ({venue.nameEn})
                    </option>
                  ))}
                </select>
                {errors.venue && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{errors.venue}</div>}
              </div>
            </div>
          </div>

          {/* Time and Duration */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#333', borderBottom: '2px solid #ffc107', paddingBottom: '10px' }}>
              時間安排
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                  開始時間
                </label>
                <input
                  type="time"
                  value={formData.timeFrom}
                  onChange={(e) => handleInputChange('timeFrom', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                  結束時間
                </label>
                <input
                  type="time"
                  value={formData.timeTo}
                  onChange={(e) => handleInputChange('timeTo', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                  活動時數
                </label>
                <input
                  type="number"
                  step="0.5"
                  min="0.5"
                  max="24"
                  value={formData.duration}
                  onChange={(e) => handleInputChange('duration', parseFloat(e.target.value))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: errors.duration ? '2px solid #dc3545' : '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                />
                {errors.duration && <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '5px' }}>{errors.duration}</div>}
              </div>
            </div>
          </div>

          {/* Participation Details */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#333', borderBottom: '2px solid #17a2b8', paddingBottom: '10px' }}>
              參與詳情
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                  參與層次
                </label>
                <select
                  value={formData.level}
                  onChange={(e) => handleInputChange('level', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  {Object.entries(slpCategories.level).map(([key, level]) => (
                    <option key={key} value={key}>
                      {level.name} ({level.nameEn})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                  參與角色
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => handleInputChange('role', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  {Object.entries(slpCategories.role).map(([key, role]) => (
                    <option key={key} value={key}>
                      {role.name} ({role.nameEn})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Achievement Level for Awards/Achievements */}
            {(formData.slpType === 'awards' || formData.slpType === 'achievements') && (
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', color: '#333' }}>
                  獎項等級
                </label>
                <select
                  value={formData.achievementLevel}
                  onChange={(e) => handleInputChange('achievementLevel', e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px'
                  }}
                >
                  <option value="">請選擇獎項等級</option>
                  {Object.entries(achievementLevels).map(([key, level]) => (
                    <option key={key} value={key}>
                      {level.icon} {level.name} ({level.nameEn}) - {level.points}分
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* People Selection */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#333', borderBottom: '2px solid #6f42c1', paddingBottom: '10px' }}>
              人員選擇
            </h3>

            {/* Students Selection */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: '#333' }}>
                參與學生 * ({formData.students?.length || 0} 人已選擇)
              </label>
              
              <div style={{
                border: errors.students ? '2px solid #dc3545' : '1px solid #ddd',
                borderRadius: '4px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                marginBottom: '10px'
              }}>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#666',
                  marginBottom: '10px',
                  minHeight: '20px'
                }}>
                  {getSelectedStudentsDisplay()}
                </div>
                
                <button
                  type="button"
                  onClick={() => setShowStudentSelector(!showStudentSelector)}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {showStudentSelector ? '收起學生列表' : '選擇學生'}
                </button>
              </div>
              
              {errors.students && <div style={{ color: '#dc3545', fontSize: '12px' }}>{errors.students}</div>}

              {/* Student List */}
              {showStudentSelector && (
                <div style={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  backgroundColor: 'white'
                }}>
                  {studentsWithNumbers.map(student => (
                    <div
                      key={student.studentNumber}
                      style={{
                        padding: '10px',
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        backgroundColor: formData.students?.includes(student.studentNumber) ? '#e3f2fd' : 'white'
                      }}
                      onClick={() => handleStudentToggle(student.studentNumber)}
                    >
                      <input
                        type="checkbox"
                        checked={formData.students?.includes(student.studentNumber) || false}
                        onChange={() => {}}
                        style={{ marginRight: '10px' }}
                      />
                      <span style={{ fontWeight: 'bold' }}>{student.name}</span>
                      <span style={{ marginLeft: '10px', color: '#666', fontSize: '12px' }}>
                        {student.studentNumber} | {student.class}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Teachers Selection */}
            <div>
              <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold', color: '#333' }}>
                負責老師 ({formData.teachersInCharge?.length || 0} 人已選擇)
              </label>
              
              <div style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '15px',
                backgroundColor: '#f8f9fa',
                marginBottom: '10px'
              }}>
                <div style={{ 
                  fontSize: '14px', 
                  color: '#666',
                  marginBottom: '10px',
                  minHeight: '20px'
                }}>
                  {getSelectedTeachersDisplay()}
                </div>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '10px'
                }}>
                  {teachers.map(teacher => (
                    <label
                      key={teacher.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        cursor: 'pointer',
                        padding: '5px'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={formData.teachersInCharge?.includes(teacher.id) || false}
                        onChange={() => handleTeacherToggle(teacher.id)}
                        style={{ marginRight: '8px' }}
                      />
                      <span style={{ fontSize: '14px' }}>{teacher.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Remarks */}
          <div style={{ marginBottom: '30px' }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#333', borderBottom: '2px solid #6c757d', paddingBottom: '10px' }}>
              備註
            </h3>

            <textarea
              value={formData.remarks}
              onChange={(e) => handleInputChange('remarks', e.target.value)}
              placeholder="請輸入活動備註或特別說明..."
              rows={4}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                resize: 'vertical'
              }}
            />
          </div>

          {/* Action Buttons */}
          <div style={{
            display: 'flex',
            gap: '15px',
            justifyContent: 'flex-end',
            paddingTop: '20px',
            borderTop: '1px solid #ddd'
          }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '12px 24px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              取消
            </button>
            
            <button
              type="submit"
              style={{
                padding: '12px 24px',
                backgroundColor: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              {isEdit ? '更新記錄' : '建立記錄'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SLPEventForm; 